import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { obtenerTecnicos, obtenerTecnicosOperativa, crearRegistroServicio } from '../servicios/api';
import ModalCrearTecnico from './ModalCrearTecnico'; // Importar el nuevo modal

const ModalCrearAviso = ({ isOpen, onClose, onAvisoCreated, initialData = null }) => {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [aparato, setAparato] = useState('');
  const [tipoRegistro, setTipoRegistro] = useState('diagnostico');
  const [avisoOrigenId, setAvisoOrigenId] = useState('');
  const [dni, setDni] = useState('');
  const [imagenes, setImagenes] = useState([]);
  const [nombreCliente, setNombreCliente] = useState('');
  const [telefonoCliente, setTelefonoCliente] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [direccionCliente, setDireccionCliente] = useState('');
  const [ubicacionCliente, setUbicacionCliente] = useState('');
  const [idTecnico, setIdTecnico] = useState('');
  const [tecnicoOperativoId, setTecnicoOperativoId] = useState('');
  const [fechaProgramada, setFechaProgramada] = useState('');
  const [horaProgramada, setHoraProgramada] = useState('');
  const [nombreEmpresa, setNombreEmpresa] = useState('');
  const [otraEmpresa, setOtraEmpresa] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [tecnicos, setTecnicos] = useState([]); // Nuevo estado para técnicos
  const [tecnicosOperativa, setTecnicosOperativa] = useState([]);
  const [isCrearTecnicoModalOpen, setIsCrearTecnicoModalOpen] = useState(false); // Nuevo estado para el modal de técnico

  const [currentUserRole, setCurrentUserRole] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);

  const handleImagenesChange = (e) => {
    const nuevasImagenes = Array.from(e.target.files || []);
    setImagenes((actuales) => {
      const clavesExistentes = new Set(
        actuales.map((imagen) => `${imagen.name}-${imagen.size}-${imagen.lastModified}`)
      );
      return [
        ...actuales,
        ...nuevasImagenes.filter((imagen) => {
          const clave = `${imagen.name}-${imagen.size}-${imagen.lastModified}`;
          if (clavesExistentes.has(clave)) return false;
          clavesExistentes.add(clave);
          return true;
        }),
      ];
    });
    e.target.value = '';
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!initialData) {
      setTipoRegistro('diagnostico');
      setAvisoOrigenId('');
      return;
    }

    const origenId = initialData.aviso_origen_id || initialData.id_aviso || initialData.id;
    setTipoRegistro('garantia');
    setAvisoOrigenId(origenId ? String(origenId) : '');
    setTitulo(`Garantía - ${initialData.aparato || 'servicio'}`);
    setDescripcion('');
    setAparato(initialData.aparato || '');
    setDni(initialData.dni || initialData.nie || '');
    setNombreCliente(initialData.nombre || '');
    setTelefonoCliente(initialData.telefono || '');
    setEmailCliente(initialData.email || '');
    setDireccionCliente(initialData.direccion || '');
    const empresaOriginal = initialData.nombre_empresa || '';
    const empresasConocidas = ['Reparamos Hogar', 'Kayandi'];
    setNombreEmpresa(empresasConocidas.includes(empresaOriginal) ? empresaOriginal : (empresaOriginal ? 'Otra' : ''));
    setOtraEmpresa(empresasConocidas.includes(empresaOriginal) ? '' : empresaOriginal);
    setIdTecnico(initialData.id_tecnico_fk ? String(initialData.id_tecnico_fk) : '');
    setTecnicoOperativoId(initialData.tecnico_id ? String(initialData.tecnico_id) : '');
    setFechaProgramada('');
    setHoraProgramada('');
    setImagenes([]);
  }, [initialData, isOpen]);

  useEffect(() => {
    const userData = localStorage.getItem('usuario');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUserRole(user.rol);
      setCurrentUserId(user.id_usuario);
      // Si el usuario es técnico, asigna el aviso a sí mismo por defecto
      if (user.rol === 'tecnico') {
        setIdTecnico(user.id_usuario);
      }
    }
  }, []); // Se ejecuta una vez al montar el componente

  // Function to fetch technicians (can be reused)
  const fetchTecnicos = useCallback(async () => {
    if (currentUserRole === 'administrador') { // Solo administradores pueden obtener la lista
      try {
        const response = await obtenerTecnicos();
        if (response.exito) {
          setTecnicos(response.tecnicos_usuarios || []);
        } else {
          console.error("Error al obtener técnicos:", response.mensaje);
        }
      } catch (error) {
        console.error("Error de red al obtener técnicos:", error);
      }
    }
  }, [currentUserRole]);

  const fetchTecnicosOperativa = useCallback(async () => {
    try {
      const response = await obtenerTecnicosOperativa();
      if (response.exito) setTecnicosOperativa(response.tecnicos || []);
    } catch (error) {
      console.error('Error al obtener técnicos de taller:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen && currentUserRole) { // Solo si el modal está abierto Y el rol ya se ha determinado
      fetchTecnicos();
    }
  }, [isOpen, currentUserRole, fetchTecnicos]); // Dependencia de isOpen y currentUserRole

  useEffect(() => {
    if (isOpen && currentUserRole === 'administrador') fetchTecnicosOperativa();
  }, [isOpen, currentUserRole, fetchTecnicosOperativa]);

  const handleTecnicoCreado = () => {
    fetchTecnicos(); // Re-fetch technicians after a new one is created
    setIsCrearTecnicoModalOpen(false); // Close the create technician modal
  };

  const manejarEnvio = async (e) => {
    e.preventDefault();
    setMensaje('');

    const token = localStorage.getItem('token');
    if (!token) {
      setMensaje('No autenticado. Por favor, inicia sesión.');
      setTipoMensaje('error');
      return;
    }

    try {
      setGuardando(true);
      const tecnicoAsignadoId = currentUserRole === 'tecnico'
        ? (idTecnico || currentUserId)
        : idTecnico;
      const datosFormulario = tipoRegistro === 'garantia'
        ? {
          tipo_registro: 'garantia',
          aviso_origen_id: avisoOrigenId,
          cliente_id: initialData.id_cliente,
          aparato,
          descripcion,
          fecha_cita: fechaProgramada,
          hora_cita: horaProgramada,
          tecnico_id: tecnicoOperativoId,
          estado: 'pendiente',
        }
        : {
          titulo,
          descripcion,
          aparato,
          tipo_registro: tipoRegistro,
          nombre: nombreCliente,
          telefono: telefonoCliente,
          email: emailCliente,
          direccion: direccionCliente,
          dni,
          id_tecnico: tecnicoAsignadoId || '',
          tecnico_id: tecnicoOperativoId,
          nombre_empresa: nombreEmpresa === 'Otra' ? otraEmpresa : nombreEmpresa,
          fecha_programada: `${fechaProgramada} ${horaProgramada}:00`,
        };
      const datosRegistro = await crearRegistroServicio(
        datosFormulario,
        tipoRegistro === 'garantia' ? [] : imagenes
      );

      if (datosRegistro.exito) {
        setMensaje(datosRegistro.mensaje);
        setTipoMensaje('exito');
        setTitulo('');
        setDescripcion('');
        setAparato('');
        setTipoRegistro('diagnostico');
        setAvisoOrigenId('');
        setDni('');
        setImagenes([]);
        setNombreCliente('');
        setTelefonoCliente('');
        setEmailCliente('');
        setDireccionCliente('');
        setUbicacionCliente('');
        setIdTecnico(currentUserRole === 'tecnico' ? currentUserId : '');
        setTecnicoOperativoId('');
        setFechaProgramada('');
        setHoraProgramada('');
        setNombreEmpresa('');
        setOtraEmpresa('');
        if (tipoRegistro === 'reparacion') {
          window.dispatchEvent(new Event('notificaciones-actualizadas'));
        }
        window.setTimeout(() => {
          onClose();
          if (onAvisoCreated) {
            onAvisoCreated();
          }
        }, 1200);
      } else {
        setMensaje(datosRegistro.mensaje || 'No se pudo guardar el registro.');
        setTipoMensaje('error');
      }
    } catch (error) {
      console.error('Error al crear el registro de servicio:', error);
      const errorMessage = error.response?.data?.mensaje
        || 'Error de conexión con el servidor. Inténtalo de nuevo más tarde.';
      setMensaje(errorMessage);
      setTipoMensaje('error');
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 p-6 rounded-lg shadow-lg w-full max-w-lg text-white max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar modal"
          title="Cerrar"
          className="absolute right-4 top-4 rounded p-1 text-gray-300 hover:bg-gray-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <X size={20} />
        </button>
        <h2 className="text-xl font-bold mb-4 text-orange-500">
          {initialData?.aviso_origen_id ? 'Programar Garantía' : 'Crear Nuevo Aviso'}
        </h2>
        {mensaje && (
          <div className={`p-2.5 mb-3 rounded text-sm ${tipoMensaje === 'exito' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
            {mensaje}
          </div>
        )}
        <form onSubmit={manejarEnvio}>
          {initialData?.aviso_origen_id ? (
            <>
              <p className="mb-4 text-sm text-slate-300">
                {nombreCliente} · {aparato} · Servicio #{avisoOrigenId}
              </p>
              <div className="mb-3">
                <label htmlFor="descripcionGarantia" className="block text-sm font-bold mb-1">Descripción del problema</label>
                <textarea
                  id="descripcionGarantia"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  rows="4"
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="fechaCitaGarantia" className="block text-sm font-bold mb-1">Fecha de la cita</label>
                <input
                  type="date"
                  id="fechaCitaGarantia"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                  value={fechaProgramada}
                  onChange={(e) => setFechaProgramada(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="horaCitaGarantia" className="block text-sm font-bold mb-1">Hora de la cita</label>
                <input
                  type="time"
                  id="horaCitaGarantia"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                  value={horaProgramada}
                  onChange={(e) => setHoraProgramada(e.target.value)}
                  required
                />
              </div>
            </>
          ) : (
            <>
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-2" role="group" aria-label="Tipo de registro">
            <button
              type="button"
              onClick={() => setTipoRegistro('diagnostico')}
              aria-pressed={tipoRegistro === 'diagnostico'}
              className={`rounded border px-3 py-2 text-sm font-semibold ${tipoRegistro === 'diagnostico' ? 'border-orange-500 bg-orange-500/20 text-orange-200' : 'border-gray-700 bg-gray-800 text-gray-300'}`}
            >
              Diagnóstico (sin factura)
            </button>
            <button
              type="button"
              onClick={() => setTipoRegistro('reparacion')}
              aria-pressed={tipoRegistro === 'reparacion'}
              className={`rounded border px-3 py-2 text-sm font-semibold ${tipoRegistro === 'reparacion' ? 'border-orange-500 bg-orange-500/20 text-orange-200' : 'border-gray-700 bg-gray-800 text-gray-300'}`}
            >
              Reparación (con factura)
            </button>
            <button
              type="button"
              onClick={() => setTipoRegistro('garantia')}
              disabled={!avisoOrigenId}
              aria-pressed={tipoRegistro === 'garantia'}
              className={`rounded border px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${tipoRegistro === 'garantia' ? 'border-orange-500 bg-orange-500/20 text-orange-200' : 'border-gray-700 bg-gray-800 text-gray-300'}`}
            >
              Garantía
            </button>
          </div>
          {tipoRegistro === 'garantia' && avisoOrigenId && (
            <p className="mb-3 text-sm text-orange-200">Servicio reparado de origen #{avisoOrigenId}</p>
          )}
          <div className="mb-3">
            <label htmlFor="titulo" className="block text-sm font-bold mb-1">Título</label>
            <input
              type="text"
              id="titulo"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="descripcion" className="block text-sm font-bold mb-1">Descripción</label>
            <textarea
              id="descripcion"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500 h-20"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              required
            ></textarea>
          </div>
          <div className="mb-3">
            <label htmlFor="aparato" className="block text-sm font-bold mb-1">Aparato</label>
            <input
              type="text"
              id="aparato"
              placeholder="Caldera, lavadora, aire acondicionado..."
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={aparato}
              onChange={(e) => setAparato(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="nombreEmpresa" className="block text-sm font-bold mb-1">Empresa</label>
            <select
              id="nombreEmpresa"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={nombreEmpresa}
              onChange={(e) => {
                setNombreEmpresa(e.target.value);
                if (e.target.value !== 'Otra') {
                  setOtraEmpresa('');
                }
              }}
              required
            >
              <option value="">Selecciona una empresa</option>
              <option value="Reparamos Hogar">Reparamos Hogar</option>
              <option value="Kayandi">Kayandi</option>
              <option value="Otra">Otra (especificar)</option>
            </select>
            {nombreEmpresa === 'Otra' && (
              <input
                type="text"
                className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500 mt-2"
                placeholder="Nombre de la empresa"
                value={otraEmpresa}
                onChange={(e) => setOtraEmpresa(e.target.value)}
                required
              />
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="nombreCliente" className="block text-sm font-bold mb-1">Nombre Cliente</label>
            <input
              type="text"
              id="nombreCliente"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={nombreCliente}
              onChange={(e) => setNombreCliente(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="dniCliente" className="block text-sm font-bold mb-1">DNI / NIF {tipoRegistro === 'reparacion' ? '*' : '(opcional)'}</label>
            <input
              type="text"
              id="dniCliente"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              required={tipoRegistro === 'reparacion'}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="telefonoCliente" className="block text-sm font-bold mb-1">Teléfono Cliente</label>
            <input
              type="text"
              id="telefonoCliente"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={telefonoCliente}
              onChange={(e) => setTelefonoCliente(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="emailCliente" className="block text-sm font-bold mb-1">Email Cliente</label>
            <input
              type="email"
              id="emailCliente"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={emailCliente}
              onChange={(e) => setEmailCliente(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="direccionCliente" className="block text-sm font-bold mb-1">Dirección Cliente</label>
            <input
              type="text"
              id="direccionCliente"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={direccionCliente}
              onChange={(e) => setDireccionCliente(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="imagenesAviso" className="block text-sm font-bold mb-1">Fotos del aparato o la avería</label>
            <input
              type="file"
              id="imagenesAviso"
              accept="image/*"
              multiple
              onChange={handleImagenesChange}
              className="block w-full text-sm text-gray-300 file:mr-3 file:rounded file:border-0 file:bg-orange-600 file:px-3 file:py-2 file:text-white hover:file:bg-orange-700"
            />
            {imagenes.length > 0 && (
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-400">
                <span>{imagenes.length} imagen(es) seleccionada(s). Puedes añadir más desde la galería.</span>
                <button type="button" onClick={() => setImagenes([])} className="shrink-0 text-orange-300 hover:text-orange-200">
                  Quitar todas
                </button>
              </div>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="ubicacionCliente" className="block text-sm font-bold mb-1">Ubicación (Piso, Puerta, etc.)</label>
            <input
              type="text"
              id="ubicacionCliente"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={ubicacionCliente}
              onChange={(e) => setUbicacionCliente(e.target.value)}
            />
          </div>
          {currentUserRole === 'administrador' && ( // Solo mostrar si es administrador
            <div className="mb-4">
              <label htmlFor="idTecnico" className="block text-gray-700 text-sm font-bold mb-2">
                Técnico:
              </label>
              <select
                id="idTecnico"
                name="idTecnico"
                value={idTecnico}
                onChange={(e) => setIdTecnico(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Selecciona un técnico</option>
                {tecnicos.map((tecnico) => (
                  <option key={tecnico.id_usuario} value={tecnico.id_usuario}> {/* Use id_usuario and nombre_usuario from API */}
                    {tecnico.nombre_usuario}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setIsCrearTecnicoModalOpen(true)}
                className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-xs"
              >
                Crear Nuevo Técnico
              </button>
            </div>
          )}
          <div className="mb-3">
            <label htmlFor="fechaProgramada" className="block text-sm font-bold mb-1">Fecha Programada</label>
            <input
              type="date"
              id="fechaProgramada"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={fechaProgramada}
              onChange={(e) => setFechaProgramada(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="horaProgramada" className="block text-sm font-bold mb-1">Hora Programada</label>
            <input
              type="time"
              id="horaProgramada"
              className="shadow appearance-none border border-gray-700 rounded w-full py-1.5 px-3 text-sm bg-gray-800 text-white leading-tight focus:outline-none focus:shadow-outline focus:ring-1 focus:ring-orange-500"
              value={horaProgramada}
              onChange={(e) => setHoraProgramada(e.target.value)}
              required
            />
          </div>
            </>
          )}
          {currentUserRole === 'administrador' && <div className="mb-3">
            <label htmlFor="tecnicoOperativo" className="block text-sm font-bold mb-1">Técnico de taller</label>
            <select
              id="tecnicoOperativo"
              value={tecnicoOperativoId}
              onChange={(e) => setTecnicoOperativoId(e.target.value)}
              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
              <option value="">Sin asignar</option>
              {tecnicosOperativa.map((tecnico) => (
                <option key={tecnico.id} value={tecnico.id}>{tecnico.nombre}</option>
              ))}
            </select>
          </div>}
          <div className="flex items-center justify-between mt-4">
            <button
              type="submit"
              disabled={guardando}
              className="bg-orange-600 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
            >
              {guardando ? 'Guardando...' : 'Guardar registro'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1.5 px-4 text-sm rounded focus:outline-none focus:shadow-outline"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>

      {/* Render ModalCrearTecnico */}
      <ModalCrearTecnico
        isOpen={isCrearTecnicoModalOpen}
        onClose={() => setIsCrearTecnicoModalOpen(false)}
        onTecnicoCreado={handleTecnicoCreado}
      />
    </div>
  );
};

export default ModalCrearAviso;